import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { blobErrMsg } from "@/lib/api";

// Plays one short clip at a time for Admin: a voice sample from the server or a provider's preview link.
// play(key, load) where load() resolves to { src, revoke }; pressing the same key again stops it.
export function useAudition() {
  const [state, setState] = useState({ key: null, phase: "idle" });
  const current = useRef({ key: null, audio: null, revoke: null });
  const token = useRef(0);

  const stop = useCallback(() => {
    token.current += 1;
    const { audio, revoke } = current.current;
    current.current = { key: null, audio: null, revoke: null };
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    if (revoke) URL.revokeObjectURL(revoke);
    setState({ key: null, phase: "idle" });
  }, []);

  const play = useCallback(async (key, load) => {
    const same = current.current.key === key;
    stop();
    if (same) return;
    const mine = token.current;
    current.current.key = key;
    setState({ key, phase: "loading" });
    let clip;
    try {
      clip = await load();
    } catch (e) {
      if (mine === token.current) stop();
      toast.error(await blobErrMsg(e));
      return;
    }
    if (mine !== token.current) {
      if (clip.revoke) URL.revokeObjectURL(clip.revoke);
      return;
    }
    const audio = new Audio(clip.src);
    current.current = { key, audio, revoke: clip.revoke || null };
    audio.onended = stop;
    audio.onerror = () => {
      toast.error("This browser could not play that audio");
      stop();
    };
    setState({ key, phase: "playing" });
    audio.play().catch((e) => {
      toast.error(`Could not play audio: ${e.message}`);
      stop();
    });
  }, [stop]);

  useEffect(() => stop, [stop]);
  return { state, play, stop };
}
